import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const WordtPartner = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    website: '',
    description: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Partner aanmelding:', formData);
  };

  const tabs = [
    { id: 'overview', label: 'Overzicht', icon: 'LayoutDashboard' },
    { id: 'add-job', label: 'Opdracht Toevoegen', icon: 'PlusCircle' },
    { id: 'add-employee', label: 'Werknemer Toevoegen', icon: 'UserPlus' },
    { id: 'become-partner', label: 'Word Partner', icon: 'Handshake' }
  ];

  const benefits = [
    { icon: 'TrendingUp', title: 'Groei Uw Netwerk', desc: 'Toegang tot duizenden vakmensen' },
    { icon: 'Euro', title: 'Verdien Commissie', desc: 'Tot 15% commissie per succesvolle match' },
    { icon: 'Users', title: 'Vind Talent', desc: 'Voeg vakmensen toe aan ons platform' },
    { icon: 'Briefcase', title: 'Plaats Personeelsvragen', desc: 'Direct toegang tot gekwalificeerde vakmensen' },
    { icon: 'Shield', title: 'Betrouwbaar Platform', desc: 'Veilige betalingen en contracten' },
    { icon: 'BarChart', title: 'Inzicht & Analytics', desc: 'Real-time statistieken en rapportages' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <MainLayout>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div variants={itemVariants} className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Users" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground">
                Wordt Partner
              </h1>
              <p className="text-muted-foreground mt-1">
                Beheer personeelsvragen, voeg vakmensen toe en groei uw netwerk
              </p>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-2">
            <div className="flex flex-wrap gap-2">
              {tabs?.map((tab) => (
                <motion.button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm
                    transition-all relative
                    ${activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }
                  `}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {activeTab === tab?.id && (
                    <motion.div
                      layoutId="activeTabIndicator"
                      className="absolute inset-0 bg-primary rounded-lg"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <span className="relative z-10 flex items-center gap-2">
                    <Icon name={tab?.icon} size={18} />
                    <span className="hidden sm:inline">{tab?.label}</span>
                  </span>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {benefits?.map((benefit, idx) => (
                  <motion.div
                    key={idx}
                    variants={itemVariants}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="bg-card rounded-xl border border-border p-6 hover:border-primary transition-all"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon name={benefit?.icon} size={24} color="var(--color-primary)" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {benefit?.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {benefit?.desc}
                    </p>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={itemVariants} className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl border border-primary/20 p-8 text-center">
                <h2 className="text-2xl font-bold text-foreground mb-4">
                  Klaar om te starten?
                </h2>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Word vandaag nog partner en krijg direct toegang tot ons platform. 
                  Plaats personeelsvragen, vind vakmensen en verdien commissie.
                </p>
                <Button size="lg" onClick={() => setActiveTab('become-partner')}>
                  <Icon name="Rocket" size={20} className="mr-2" />
                  Word Nu Partner
                </Button>
              </motion.div>
            </motion.div>
          )}

          {activeTab === 'add-job' && (
            <motion.div
              key="add-job"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-card rounded-xl border border-border p-6 lg:p-8"
            >
              <h2 className="text-xl font-semibold text-foreground mb-6">
                Nieuwe Personeelsvraag Toevoegen
              </h2>
              <form className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Personeelsvraag Titel *
                  </label>
                  <Input type="text" placeholder="Bijv. Vakmensen gezocht voor zonnepanelenproject" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Categorie *
                  </label>
                  <Select required>
                    <option value="">Selecteer categorie</option>
                    <option value="solar">Zonnepanelen</option>
                    <option value="heatpump">Warmtepompen</option>
                    <option value="battery">Thuisbatterijen</option>
                    <option value="maintenance">Technisch onderhoud</option>
                    <option value="ventilation">Ventilatie & klimaat</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Beschrijving *
                  </label>
                  <textarea
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground min-h-[120px]"
                    placeholder="Gedetailleerde beschrijving..."
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Budget (€)
                    </label>
                    <Input type="number" placeholder="Geschat budget" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Locatie *
                    </label>
                    <Input type="text" placeholder="Stad of postcode" required />
                  </div>
                </div>
                <Button type="submit">
                  <Icon name="Send" size={18} className="mr-2" />
                  Personeelsvraag Toevoegen
                </Button>
              </form>
            </motion.div>
          )}

          {activeTab === 'add-employee' && (
            <motion.div
              key="add-employee"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-card rounded-xl border border-border p-6 lg:p-8"
            >
              <h2 className="text-xl font-semibold text-foreground mb-6">
                Vakman Toevoegen aan Platform
              </h2>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Voornaam *
                    </label>
                    <Input type="text" placeholder="Voornaam" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Achternaam *
                    </label>
                    <Input type="text" placeholder="Achternaam" required />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Email *
                    </label>
                    <Input type="email" placeholder="email@voorbeeld.nl" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Telefoon *
                    </label>
                    <Input type="tel" placeholder="+31 6 12345678" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Specialisatie *
                  </label>
                  <Select required>
                    <option value="">Selecteer specialisatie</option>
                    <option value="electrician">Elektricien</option>
                    <option value="plumber">Loodgieter</option>
                    <option value="hvac">HVAC Technicus</option>
                    <option value="maintenance">Onderhoudsmonteur</option>
                    <option value="cleaner">Schoonmaker</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Ervaring (jaren)
                  </label>
                  <Input type="number" placeholder="Aantal jaren ervaring" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Certificaten & Kwalificaties
                  </label>
                  <textarea
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground min-h-[100px]"
                    placeholder="Lijst van certificaten en kwalificaties..."
                  />
                </div>
                <Button type="submit">
                  <Icon name="UserPlus" size={18} className="mr-2" />
                  Werknemer Toevoegen
                </Button>
              </form>
            </motion.div>
          )}

          {activeTab === 'become-partner' && (
            <motion.div
              key="become-partner"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-card rounded-xl border border-border p-6 lg:p-8"
            >
              <h2 className="text-xl font-semibold text-foreground mb-6">
                Partner Aanmeldingsformulier
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Bedrijfsnaam *
                  </label>
                  <Input
                    type="text"
                    placeholder="Uw bedrijfsnaam"
                    value={formData?.companyName}
                    onChange={(e) => handleChange('companyName', e?.target?.value)}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Contactpersoon *
                    </label>
                    <Input
                      type="text"
                      placeholder="Naam contactpersoon"
                      value={formData?.contactPerson}
                      onChange={(e) => handleChange('contactPerson', e?.target?.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      placeholder="email@bedrijf.nl"
                      value={formData?.email}
                      onChange={(e) => handleChange('email', e?.target?.value)}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Telefoon *
                    </label>
                    <Input
                      type="tel"
                      placeholder="+31 6 12345678"
                      value={formData?.phone}
                      onChange={(e) => handleChange('phone', e?.target?.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Website
                    </label>
                    <Input
                      type="url"
                      placeholder="https://www.uwbedrijf.nl"
                      value={formData?.website}
                      onChange={(e) => handleChange('website', e?.target?.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Bedrijfsomschrijving *
                  </label>
                  <textarea
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground min-h-[120px]"
                    placeholder="Vertel ons over uw bedrijf en waarom u partner wilt worden..."
                    value={formData?.description}
                    onChange={(e) => handleChange('description', e?.target?.value)}
                    required
                  />
                </div>
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-muted-foreground">
                      <p className="font-medium text-foreground mb-1">Wat gebeurt er na aanmelding?</p>
                      <p>Ons team beoordeelt uw aanvraag binnen 2 werkdagen. Na goedkeuring ontvangt u toegang tot het partner dashboard.</p>
                    </div>
                  </div>
                </div>
                <Button type="submit" size="lg">
                  <Icon name="Send" size={18} className="mr-2" />
                  Aanmelding Versturen
                </Button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </MainLayout>
  );
};

export default WordtPartner;